/**
 * anexoBCierreDemo.data.ts — Cierre aislado para validar el formato oficial Anexo B.
 *
 * Conserva los datos contables del escenario canónico, pero usa IDs, sesión y fecha
 * propios para que aparezca como una jornada independiente en el historial.
 */

import type {
  CierreCaja,
  TransferenciasVerificacion,
  Venta,
  VentaProducto,
} from "../types/caja.types";
import {
  buildMay12CierreCaja,
  MAY12_SESION_MAIN_ID,
} from "./may12CajaDemoSeed.data";
import { calcResumenTransferencias } from "../utils/transferenciasResumen";

export const ANEXO_B_CIERRE_DEMO_ID = "demo-anexo-b-cierre-2026-06-02";
export const ANEXO_B_SESION_DEMO_ID = "sesion_demo_anexo_b_2026_06_02";
export const ANEXO_B_DEMO_VERSION = "2026-06-02-anexo-b-v5";

const SOURCE_DAY = "2026-05-12";
const ANEXO_B_DAY = "2026-06-02";
const ID_REFERENCE_KEYS = new Set(["id", "ventaId", "creditoId"]);
const ANEXO_B_OPENED_AT = "2026-06-02T07:55:00.000-05:00";
const ANEXO_B_CLOSED_AT = "2026-06-02T09:22:00.000-05:00";

type MetodoMonto = Record<
  "efectivo" | "nequi" | "daviplata" | "tarjeta",
  number
>;

type AnexoVentaSpec = {
  employeeId: string;
  employeeName: string;
  total: number;
  service: MetodoMonto;
  featured?: Array<[string, number]>;
  fillerCount: number;
  /** Descuento global % aplicado a toda la venta (auditoría del ticket). */
  descuentoGlobalPct?: number;
  /** Descuento por ítem en pesos aplicado a la venta. */
  descuentoItems?: number;
  /** Incentivo por método de pago (descuento al cliente al cobrar). */
  incentivoMetodo?: {
    metodoId: string;
    metodoLabel: string;
    pct: number;
    descuentoTotal: number;
  };
  /** Marca la(s) línea(s) destacadas con precio especial. */
  precioEspecial?: "mayorista" | "vip";
  /** Fidelización: puntos ganados, saldo y premios canjeados en la venta. */
  fidelizacion?: {
    clienteId: string;
    nombre: string;
    codigo: string;
    saldoAntes: number;
    puntosGanados: number;
    premios?: Array<{ nombre: string; puntos: number }>;
  };
};

const splitHalf = (amounts: MetodoMonto, second: boolean): MetodoMonto =>
  Object.fromEntries(
    Object.entries(amounts).map(([method, amount]) => [
      method,
      second ? amount - Math.floor(amount / 2) : Math.floor(amount / 2),
    ]),
  ) as MetodoMonto;

const sumMetodos = (amounts: MetodoMonto): number =>
  Object.values(amounts).reduce((total, amount) => total + amount, 0);

const JUAN_SERVICE: MetodoMonto = {
  efectivo: 109_247,
  nequi: 85_799,
  daviplata: 15_971,
  tarjeta: 54_230,
};

const ANEXO_B_VENTA_SPECS: AnexoVentaSpec[] = [
  {
    employeeId: "emp_1",
    employeeName: "Juan Pérez",
    total: 1_077_530,
    service: splitHalf(JUAN_SERVICE, false),
    featured: [["Reloj Deportivo Hombre", 499_000]],
    fillerCount: 5,
    descuentoGlobalPct: 5,
  },
  {
    employeeId: "emp_1",
    employeeName: "Juan Pérez",
    total: 1_077_530,
    service: splitHalf(JUAN_SERVICE, true),
    featured: [["Reloj Deportivo Hombre", 499_000]],
    fillerCount: 5,
    fidelizacion: {
      clienteId: "cl_cg2020",
      nombre: "Carlos Giraldo",
      codigo: "CG2020",
      saldoAntes: 1200,
      puntosGanados: 300,
    },
  },
  {
    employeeId: "emp_3",
    employeeName: "Carlos Ruiz",
    total: 528_772,
    service: {
      efectivo: 26_703,
      nequi: 22_999,
      daviplata: 3_605,
      tarjeta: 11_063,
    },
    featured: [["Gafas de Sol Unisex", 449_000]],
    fillerCount: 1,
  },
  {
    employeeId: "emp_3",
    employeeName: "Carlos Ruiz",
    total: 528_772,
    service: {
      efectivo: 26_703,
      nequi: 22_999,
      daviplata: 3_605,
      tarjeta: 11_063,
    },
    featured: [["Gafas de Sol Unisex", 449_000]],
    fillerCount: 1,
  },
  {
    employeeId: "emp_3",
    employeeName: "Carlos Ruiz",
    total: 528_771,
    service: {
      efectivo: 26_704,
      nequi: 22_998,
      daviplata: 3_604,
      tarjeta: 11_064,
    },
    fillerCount: 5,
    descuentoItems: 25_000,
  },
  {
    employeeId: "emp_4",
    employeeName: "Dylan Torres",
    total: 850_000,
    service: {
      efectivo: 45_000,
      nequi: 30_000,
      daviplata: 6_000,
      tarjeta: 25_000,
    },
    featured: [["Batería Auto 12V 70Ah", 589_000]],
    fillerCount: 2,
    incentivoMetodo: {
      metodoId: "nequi",
      metodoLabel: "Nequi",
      pct: 2,
      descuentoTotal: 17_000,
    },
    precioEspecial: "mayorista",
  },
  {
    employeeId: "emp_5",
    employeeName: "Esteban Gómez",
    total: 620_000,
    service: {
      efectivo: 38_000,
      nequi: 25_000,
      daviplata: 5_155,
      tarjeta: 14_000,
    },
    featured: [["Gafas de Sol Unisex (Negro)", 449_000]],
    fillerCount: 1,
    fidelizacion: {
      clienteId: "cl_ps1065",
      nombre: "Pedro Sánchez",
      codigo: "PS1065",
      saldoAntes: 500,
      puntosGanados: 480,
      premios: [{ nombre: "Descuento 10%", puntos: 50 }],
    },
  },
  {
    employeeId: "emp_6",
    employeeName: "Sofía Castro",
    total: 480_000,
    service: {
      efectivo: 29_000,
      nequi: 20_000,
      daviplata: 8_000,
      tarjeta: 10_000,
    },
    featured: [["Perfume Hombre CK", 349_000]],
    fillerCount: 1,
  },
  {
    employeeId: "emp_2",
    employeeName: "Yinet Alarcón",
    total: 410_000,
    service: {
      efectivo: 25_000,
      nequi: 18_000,
      daviplata: 3_000,
      tarjeta: 10_000,
    },
    fillerCount: 3,
  },
  {
    employeeId: "emp_7",
    employeeName: "José Delgado",
    total: 350_000,
    service: {
      efectivo: 21_000,
      nequi: 15_000,
      daviplata: 5_000,
      tarjeta: 8_000,
    },
    fillerCount: 3,
    precioEspecial: "vip",
  },
  {
    employeeId: "emp_9",
    employeeName: "Miriam Beltrán",
    total: 310_000,
    service: {
      efectivo: 18_000,
      nequi: 12_000,
      daviplata: 3_000,
      tarjeta: 10_000,
    },
    fillerCount: 2,
  },
  {
    employeeId: "emp_10",
    employeeName: "Luz Marina",
    total: 280_000,
    service: {
      efectivo: 14_000,
      nequi: 14_000,
      daviplata: 2_000,
      tarjeta: 6_000,
    },
    fillerCount: 2,
    fidelizacion: {
      clienteId: "cl_ml2010",
      nombre: "María López",
      codigo: "ML2010",
      saldoAntes: 1000,
      puntosGanados: 760,
      premios: [{ nombre: "Producto gratis", puntos: 200 }],
    },
  },
  {
    employeeId: "emp_11",
    employeeName: "Diana Pinzón",
    total: 200_000,
    service: { efectivo: 10_000, nequi: 10_000, daviplata: 0, tarjeta: 5_758 },
    fillerCount: 3,
  },
];

function buildAnexoBVentas(): Venta[] {
  const paymentRemaining: MetodoMonto = {
    efectivo: 3_058_427,
    nequi: 2_312_396,
    daviplata: 520_684,
    tarjeta: 1_349_868,
  };
  let fillerIndex = 1;

  return ANEXO_B_VENTA_SPECS.map((spec, index) => {
    const subtotal = spec.total - sumMetodos(spec.service);
    const featured = spec.featured ?? [];
    const featuredTotal = featured.reduce(
      (total, [, amount]) => total + amount,
      0,
    );
    const fillerTotal = subtotal - featuredTotal;
    const fillerBase = Math.floor(fillerTotal / spec.fillerCount);
    const products: VentaProducto[] = [
      ...featured.map(([nombre, precioUnitario], productIndex) => ({
        productoId: `anexo-b-top-${index + 1}-${productIndex + 1}`,
        nombre,
        cantidad: 1,
        precioUnitario,
        ...(spec.precioEspecial === "mayorista" ? { esMayorista: true } : {}),
        ...(spec.precioEspecial === "vip" ? { esVIP: true } : {}),
      })),
      ...Array.from({ length: spec.fillerCount }, (_, fillerOffset) => ({
        productoId: `anexo-b-complemento-${fillerIndex++}`,
        nombre: `Producto complementario ${fillerIndex - 1}`,
        cantidad: 1,
        precioUnitario:
          fillerBase +
          (fillerOffset === spec.fillerCount - 1
            ? fillerTotal % spec.fillerCount
            : 0),
      })),
    ];

    let paymentToAllocate = spec.total;
    const breakdown = (
      Object.keys(paymentRemaining) as Array<keyof MetodoMonto>
    )
      .map((method) => {
        const amount = Math.min(paymentToAllocate, paymentRemaining[method]);
        paymentRemaining[method] -= amount;
        paymentToAllocate -= amount;
        return { method, amount };
      })
      .filter((payment) => payment.amount > 0);

    const descuentosGlobal = spec.descuentoGlobalPct
      ? Math.round(subtotal * (spec.descuentoGlobalPct / 100))
      : 0;

    const venta: Venta = {
      id: `anexo-b-venta-${String(index + 1).padStart(2, "0")}`,
      fecha: `2026-06-02T08:${String(index + 1).padStart(2, "0")}:00.000-05:00`,
      total: spec.total,
      subtotal,
      servicioAmount: sumMetodos(spec.service),
      servicioPct: 0,
      servicioPorMetodo: spec.service,
      metodoPago: "mixto",
      detalleMetodoPago: { breakdown },
      productos: products,
      usuarioId: spec.employeeId,
      userName: spec.employeeName,
      estado: "activa",
      sesionId: ANEXO_B_SESION_DEMO_ID,
      puntoCajaId: "pc1",
      puntoCajaNombre: "Caja principal",
    };

    if (descuentosGlobal > 0 || spec.descuentoItems || spec.incentivoMetodo) {
      venta.descuentos = {
        totalDescuentoItems: spec.descuentoItems ?? 0,
        descuentoGlobal: descuentosGlobal,
        ...(spec.descuentoGlobalPct
          ? { globalDiscountPct: spec.descuentoGlobalPct }
          : {}),
        ...(spec.incentivoMetodo
          ? {
              incentivoMetodoPago: {
                metodoId: spec.incentivoMetodo.metodoId,
                metodoLabel: spec.incentivoMetodo.metodoLabel,
                pct: spec.incentivoMetodo.pct,
                descuentoTotal: spec.incentivoMetodo.descuentoTotal,
                subtotalAntesIncentivo: spec.total,
              },
            }
          : {}),
      };
    }

    if (spec.fidelizacion) {
      venta.clientePuntosId = spec.fidelizacion.clienteId;
      venta.clientePuntosNombre = spec.fidelizacion.nombre;
      venta.clientePuntosCodigo = spec.fidelizacion.codigo;
      venta.saldoAntesCanje = spec.fidelizacion.saldoAntes;
      venta.puntosGanados = spec.fidelizacion.puntosGanados;
      venta.puntosNuevoSaldo =
        spec.fidelizacion.saldoAntes +
        spec.fidelizacion.puntosGanados -
        (spec.fidelizacion.premios?.reduce((s, p) => s + p.puntos, 0) ?? 0);
      if (spec.fidelizacion.premios?.length) {
        venta.premiosCanjeados = spec.fidelizacion.premios;
      }
    }

    return venta;
  });
}

function buildAnexoBAuditoriaDigital(
  ventas: Venta[],
): TransferenciasVerificacion {
  const resumen = calcResumenTransferencias(ventas);
  const auditTimes: Record<string, string> = {
    tarjeta: "2026-06-02T09:26:00.000-05:00",
    nequi: "2026-06-02T09:35:00.000-05:00",
    daviplata: "2026-06-02T09:38:00.000-05:00",
  };
  const porMetodo: TransferenciasVerificacion["porMetodo"] = {};

  Object.entries(resumen).forEach(([method, meta]) => {
    const mismatch = method === "nequi";
    const declarado = mismatch ? Math.max(0, meta.total - 32_396) : meta.total;
    const declaraciones = mismatch
      ? [
          Math.max(0, meta.total - 212_396),
          Math.max(0, meta.total - 62_396),
          declarado,
        ]
      : [meta.total];
    const finalTime = new Date(
      auditTimes[method] ?? "2026-06-02T09:30:00.000-05:00",
    ).getTime();

    porMetodo[method] = {
      label: meta.label,
      esperado: meta.total,
      totalCobrado: meta.totalCobrado,
      totalTarifas: meta.totalTarifas,
      cantidadEsperada: meta.count,
      declarado,
      diferencia: declarado - meta.total,
      cuadrado: !mismatch,
      itemsVerificados: meta.items.map((item) => item.id),
      sumaComprobantesMarcados: meta.total,
      intentos: declaraciones.map((valor, index) => ({
        numero: index + 1,
        declarado: valor,
        diferencia: valor - meta.total,
        registradoEn: new Date(
          finalTime - (declaraciones.length - index - 1) * 2 * 60_000,
        ).toISOString(),
      })),
      intentosAgotados: mismatch,
      comprobantesConfirmados: meta.count,
      auditoriaComprobantesCompletada: true,
      motivoDescuadre: mismatch ? "error_suma" : undefined,
      detalleDescuadre: mismatch ? "Se omitió un comprobante" : undefined,
    };
  });

  return {
    porMetodo,
    declaracionAceptada: true,
    todoCuadrado: false,
    listoParaCerrar: true,
  };
}

function cloneAnexoValue(value: unknown, key?: string): unknown {
  if (typeof value === "string") {
    if (value.startsWith(SOURCE_DAY)) {
      return `${ANEXO_B_DAY}${value.slice(SOURCE_DAY.length)}`;
    }
    if (value === MAY12_SESION_MAIN_ID) return ANEXO_B_SESION_DEMO_ID;
    if (key && ID_REFERENCE_KEYS.has(key)) return `anexo-b-${value}`;
    if (value.startsWith("may12-"))
      return `anexo-b-${value.slice("may12-".length)}`;
    return value;
  }
  if (Array.isArray(value)) return value.map((item) => cloneAnexoValue(item));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        cloneAnexoValue(item, key),
      ]),
    );
  }
  return value;
}

/** Un único cierre oficial, separado del consolidado multicaja del día actual. */
export function buildAnexoBCierreDemo(): CierreCaja {
  const canonical = buildMay12CierreCaja();
  const cloned = cloneAnexoValue(canonical) as CierreCaja;
  const ventasSnapshot = buildAnexoBVentas();

  return {
    ...cloned,
    id: ANEXO_B_CIERRE_DEMO_ID,
    sesionId: ANEXO_B_SESION_DEMO_ID,
    abiertaEn: ANEXO_B_OPENED_AT,
    cerradaEn: ANEXO_B_CLOSED_AT,
    ventasCount: ventasSnapshot.length,
    ventasSnapshot,
    transferenciasVerificacion: buildAnexoBAuditoriaDigital(ventasSnapshot),
    extraNote: "Demo independiente del formato oficial Anexo B.",
  };
}
